"use client";

import { useState, useEffect, useRef } from "react";
import "./AdvertisingAndMediaSection1.css";
import laptopImage from "../../../assets/LDH_Laptop_Mockup.png"; // Replace with the correct path to your laptop image
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faPersonHiking } from "@fortawesome/free-solid-svg-icons";
import { faMessage, faLightbulb } from "@fortawesome/free-regular-svg-icons";
import { useTranslation } from "react-i18next";

const AdvertisingAndMediaSection1 = () => {
  const [activeBrandSlide, setActiveBrandSlide] = useState(0);
  const brandsSliderRef = useRef(null);
  const brandItemRefs = useRef([]);

  const { t } = useTranslation("global");
  // build your brands array from translations
  const brands = [
    {
      icon: faBook,
      title: t("advertisingAndMediaSection1.brands.first.title"),
      description: t("advertisingAndMediaSection1.brands.first.description"),
    },
    {
      icon: faMessage,
      title: t("advertisingAndMediaSection1.brands.second.title"),
      description: t("advertisingAndMediaSection1.brands.second.description"),
    },
    {
      icon: faPersonHiking,
      title: t("advertisingAndMediaSection1.brands.third.title"),
      description: t("advertisingAndMediaSection1.brands.third.description"),
    },
    {
      icon: faLightbulb,
      title: t("advertisingAndMediaSection1.brands.fourth.title"),
      description: t("advertisingAndMediaSection1.brands.fourth.description"),
    },
  ];

  // Set up intersection observer to detect which brand item is in view
  useEffect(() => {
    // Only set up the observer on mobile devices
    if (window.innerWidth < 992) {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.6, // Brand item is considered in view when 60% visible
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = brandItemRefs.current.findIndex(
              (ref) => ref === entry.target
            );
            if (index !== -1) {
              setActiveBrandSlide(index);
            }
          }
        });
      }, options);

      const currentRefs = brandItemRefs.current;
      // Observe all brand item elements
      currentRefs.forEach((item) => {
        if (item) observer.observe(item);
      });

      return () => {
        currentRefs.forEach((item) => {
          if (item) observer.unobserve(item);
        });
      };
    }
  }, []);

  // Scroll to a specific brand item when indicator is clicked
  const scrollToBrandItem = (index) => {
    if (brandItemRefs.current[index]) {
      brandItemRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
      setActiveBrandSlide(index);
    }
  };

  return (
    <main className="marketing-plan" aria-labelledby="marketing-plan-heading">
      <section
        className="hero-section"
        aria-label="Marketing Plan Introduction"
      >
        <div className="text-content-adv">
          <h1 id="marketing-plan-heading">
            {t("advertisingAndMediaSection1.marketingPlan.part1")}{" "}
            <span className="highlight-advertising">
              {t("advertisingAndMediaSection1.marketingPlan.highlight")}
            </span>
          </h1>
        </div>
      </section>
      <div className="image-container-advertising">
        <img
          src={laptopImage || "/placeholder.svg"}
          alt={t("advertisingAndMediaSection1.laptopImageAlt")}
          className="laptop-image"
          loading="lazy"
          title="Virtue Marketing Solutions"
        />
      </div>

      {/* Brands section with slider */}
      <div className="brands-section">
        <section
          className="brands"
          ref={brandsSliderRef}
          aria-label="Marketing Services"
        >
          {brands.map((brand, index) => (
            <article
              key={index}
              className="brand"
              ref={(el) => (brandItemRefs.current[index] = el)}
              id={`brand-${index}`}
            >
              <h2>
                <FontAwesomeIcon icon={brand.icon} aria-hidden="true" />{" "}
                {brand.title}
              </h2>
              <p>{brand.description}</p>
            </article>
          ))}
        </section>

        {/* Slider indicators for mobile */}
        <div
          className="brands-slider-indicators"
          role="tablist"
          aria-label="Brand navigation"
        >
          {brands.map((brand, index) => (
            <div
              key={index}
              className={`slider-dot ${
                activeBrandSlide === index ? "active" : ""
              }`}
              onClick={() => scrollToBrandItem(index)}
              role="tab"
              aria-selected={activeBrandSlide === index}
              aria-controls={`brand-${index}`}
              aria-label={`Navigate to ${brand.title}`}
              tabIndex={0}
            />
          ))}
        </div>
      </div>
    </main>
  );
};

export default AdvertisingAndMediaSection1;
